import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

import { api } from '../api';
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (email && password) {
       try {
         await api.post('/auth/login', { email, password });
         toast.success("Successfully logged in");
         await refreshUser();
         navigate('/chat');
       } catch (err) {
         console.error(err);
         toast.error("Login failed");
       }
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin; 
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[rgb(var(--bg-0))] relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute inset-0 bg-[radial-gradient(1000px_circle_at_20%_-10%,rgba(0,194,255,0.12),transparent_55%),radial-gradient(900px_circle_at_90%_0%,rgba(255,255,255,0.03),transparent_50%)] pointer-events-none" />
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch"/></filter><rect width="160" height="160" filter="url(%23n)" opacity="0.45"/></svg>')` }}></div>

      <div className="w-full max-w-md z-10 px-4">
        <div className="mb-10 text-center">
          {/* Logo Placeholder - replace with actual image if available */}
          <div className="flex justify-center mb-6">
             <img src="/assets/logo.png" alt="AtCoastal" className="h-12 w-auto drop-shadow-[0_0_15px_rgba(0,194,255,0.3)]" />
          </div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">AtCoastal.AI</h1>
          <p className="text-white/40 text-sm mt-2">Sign in to your brokerage intelligence workspace</p>
        </div>
        
        <div className="lg-glass-strong rounded-3xl p-8 border border-white/10 shadow-2xl backdrop-blur-xl">
             <div className="space-y-6">
                <Button 
                  variant="outline" 
                  className="w-full flex gap-3 h-11 bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white font-medium" 
                  onClick={handleGoogleLogin}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                     <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                     <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                     <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                     <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  Sign in with Google
                </Button>
                
                <div className="relative">
                   <div className="absolute inset-0 flex items-center">
                     <span className="w-full border-t border-white/10" />
                   </div>
                   <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
                     <span className="bg-[#0b101b] px-2 text-white/30">Or use email</span>
                   </div>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white/60 text-xs uppercase tracking-wide">Email</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      placeholder="agent@atcoastal.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-black/20 border-white/10 text-white placeholder:text-white/20 focus-visible:ring-cyan-500/50 h-11"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-white/60 text-xs uppercase tracking-wide">Password</Label>
                    <Input 
                      id="password" 
                      type="password" 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-black/20 border-white/10 text-white focus-visible:ring-cyan-500/50 h-11"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11 bg-cyan-500 hover:bg-cyan-400 text-black font-bold shadow-[0_0_20px_rgba(0,194,255,0.3)] transition-all" data-testid="login-form-submit-button">
                    Sign In
                  </Button>
                </form>
             </div>
        </div>
        
        <div className="text-center mt-8 text-white/20 text-xs">
           &copy; 2025 AtCoastal Realty. Secured by Emergent.
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
